import React, { useEffect, useState, Component } from 'react';
import { LogIn, X, UserRound, Star, Ticket, Repeat, ArrowRight, ScanFace, Apple } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { useStore, normalizePhone, levelForPoints } from '../lib/store';
import type { RegisteredUser, User } from '../lib/store';
/** Inline Google "G" mark (lucide has no brand glyph). */
function GoogleMark({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-448" data-mp="3p6ionm0rsyl0">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" data-id="element-449" data-mp="3p57lnof5fuuy" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" data-id="element-450" data-mp="3f8ckexrhrgcz" />
      <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" data-id="element-451" data-mp="23sxq61qo32lk" />
      <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.29 9.14 4.75 12 4.75Z" data-id="element-452" data-mp="g16etktqg2pm" />
    </svg>;
}
/**
 * Bottom-right login entry for RECURRING users. Lets someone who already has an
 * account sign back in with just their name + phone — no catch code required —
 * so they can review, reserve or visit their profile. On success it sets the
 * user (as "returning") and routes straight into the app.
 */
export function RecurringLogin({
  onLogin
}: {
  onLogin?: () => void;
}) {
  const {
    setUser,
    registeredUsers,
    hydrateUserByPhone
  } = useStore();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Passwordless provider the user picked (drives the headline + button copy).
  // null = the default name + phone flow.
  const [provider, setProvider] = useState<'faceid' | 'apple' | 'google' | null>(null);
  // Whether Face ID / passkey hardware is plausibly available on this device.
  const passkeySupported = typeof window !== 'undefined' && !!(window as any).PublicKeyCredential;
  // Lock body scroll + Esc-to-close while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);
  const close = () => {
    setOpen(false);
    window.setTimeout(() => {
      setError('');
      setProvider(null);
    }, 250);
  };
  // Face ID / passkey fast-path. We can't run a real WebAuthn ceremony without
  // registered credentials + a backend, so when a passkey was previously saved
  // for a recognised number we log straight in; otherwise we fall back to the
  // name + phone confirmation (still passwordless). No SMS/OTP is ever used.
  const handleFaceId = async () => {
    setProvider('faceid');
    setError('');
    if (recognized && passkeySupported) {
      setIsLoading(true);
      try {
        await (navigator as any).credentials?.get?.({
          publicKey: {
            challenge: crypto.getRandomValues(new Uint8Array(16)),
            timeout: 20000,
            userVerification: 'preferred',
            rpId: window.location.hostname,
            allowCredentials: []
          }
        });
      } catch {
        /* user cancelled or no credential — fall through to confirm */
      }
      setIsLoading(false);
    }
  };
  // Recognise a returning user the moment their phone matches a known account.
  const recognized: RegisteredUser | null = phone.trim() ? registeredUsers.find(u => normalizePhone(u.phone) === normalizePhone(phone)) || null : null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || phone.replace(/\D/g, '').length < 6) {
      setError('Enter the phone number you registered with.');
      return;
    }
    if (!recognized && (!firstName.trim() || !lastName.trim())) {
      setError('Enter your name and phone to log in.');
      return;
    }
    setError('');
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 400));
    const fullName = `${firstName} ${lastName}`.trim() || recognized?.name || 'Member';
    const loggedInUser: User = {
      phone,
      name: fullName,
      ageRange: recognized?.ageRange,
      city: recognized?.city,
      occupation: recognized?.occupation,
      profession: recognized?.profession,
      workArea: recognized?.workArea,
      favoriteArea: recognized?.favoriteArea,
      interests: recognized?.interests,
      storePreference: recognized?.storePreference,
      customAnswers: recognized?.customAnswers,
      invitedBy: recognized?.invitedBy,
      partnerCode: recognized?.partnerCode,
      points: recognized?.points ?? 0,
      level: recognized?.level ?? levelForPoints(recognized?.points ?? 0),
      eligible_for_next_event: recognized?.eligible_for_next_event ?? false
    };
    // Mark as returning so catch-count logic treats them as a regular.
    setUser(loggedInUser, true);
    // Pull this person's SAVED points + full catch/review history back from the
    // shared sheet so their activity returns even on a fresh device/browser.
    await hydrateUserByPhone(phone);
    setIsLoading(false);
    close();
    onLogin?.();
  };
  return <>
      {/* Floating trigger — bottom-right */}
      <button onClick={() => setOpen(true)} aria-label="Log in — returning member" className="fixed bottom-5 right-5 z-30 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-brand-blue text-white shadow-lg shadow-brand-navy/25 hover:bg-brand-blue-dark active:translate-y-0.5 transition-all" data-id="element-453" data-mp="2a6ekmvbdmzku">
        <LogIn className="w-5 h-5 text-brand-yellow" data-id="element-454" data-mp="gqjzfrk7397h" />
        <span className="font-display tracking-wide text-base" data-id="element-455" data-mp="bwyx5nbkjt2h">Log in</span>
      </button>

      {open && <div role="dialog" aria-modal="true" aria-label="Member login" className="fixed inset-0 z-[100] flex items-end sm:items-center sm:justify-center" data-id="element-456" data-mp="6xm8qy51udxs">
          {/* Backdrop */}
          <button aria-label="Close" onClick={close} className="absolute inset-0 bg-brand-navy/70 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-457" data-mp="3u5gagwwvr8i9" />

          {/* Panel */}
          <div className="relative z-10 w-full sm:max-w-md bg-white rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300 max-h-screen" data-id="element-458" data-mp="1ncvxe7ouxzf0">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0" data-id="element-459" data-mp="1pxveby30xbm5">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-blue" data-id="element-460" data-mp="1uvn1yfwmybkj">
                Returning member
              </span>
              <button onClick={close} aria-label="Close" className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" data-id="element-461" data-mp="1zp848k59hrpj">
                <X className="w-4 h-4 text-gray-600" data-id="element-462" data-mp="1bx1192516mh5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-7" data-id="element-463" data-mp="1gwx591dqadwp">
              <div className="w-16 h-16 rounded-2xl bg-brand-blue flex items-center justify-center mb-5 shadow-lg shadow-brand-blue/30" data-id="element-464" data-mp="1gvcc9g72b30y">
                <UserRound className="w-8 h-8 text-brand-yellow" data-id="element-465" data-mp="2sc2l772ve8y9" />
              </div>
              <h2 className="text-3xl font-display text-brand-navy leading-tight mb-1" data-id="element-466" data-mp="2xavoy7scull8">
                {recognized ? `Welcome back, ${recognized.name.split(' ')[0]}` : 'Welcome back'}
              </h2>
              <p className="text-gray-500 text-sm mb-5" data-id="element-467" data-mp="3dgwcu6q2k1lu">
                Log in to review, reserve or check your profile — no password or
                SMS code needed.
              </p>

              {/* What you can do once in */}
              <div className="grid grid-cols-3 gap-2 mb-6" data-id="element-468" data-mp="3ncpu23eqgj9y">
                <Perk icon={Star} label="Review" data-id="element-469" data-mp="4z073a2et114" />
                <Perk icon={Ticket} label="Reserve" data-id="element-470" data-mp="51w5ezi65rq3" />
                <Perk icon={Repeat} label="Profile" data-id="element-471" data-mp="xepxciue0l4" />
              </div>

              {/* Passwordless providers — pick one, then confirm identity */}
              <div className="space-y-2.5 mb-5" data-id="element-472" data-mp="9z4p54iyu3tg">
                {passkeySupported && <button type="button" onClick={handleFaceId} aria-pressed={provider === 'faceid'} className={`w-full flex items-center justify-center gap-2.5 rounded-xl py-3 text-sm font-bold border-2 transition-colors ${provider === 'faceid' ? 'border-brand-blue bg-brand-blue/5 text-brand-blue' : 'border-gray-200 text-brand-navy hover:bg-gray-50'}`} data-id="element-473" data-mp="9xbivqc9lllz">
                    <ScanFace className="w-5 h-5" data-id="element-474" data-mp="hhvu3dunohzi" />
                    Continue with Face ID
                  </button>}
                <button type="button" onClick={() => {
              setProvider('apple');
              setError('');
            }} aria-pressed={provider === 'apple'} className="w-full flex items-center justify-center gap-2.5 rounded-xl py-3 text-sm font-bold bg-black text-white hover:bg-gray-900 transition-colors" data-id="element-475" data-mp="mfnnydev2e6c">
                  <Apple className="w-5 h-5" data-id="element-476" data-mp="1sy2cizmqzr76" />
                  Continue with Apple
                </button>
                <button type="button" onClick={() => {
              setProvider('google');
              setError('');
            }} aria-pressed={provider === 'google'} className="w-full flex items-center justify-center gap-2.5 rounded-xl py-3 text-sm font-bold bg-white border-2 border-gray-200 text-brand-navy hover:bg-gray-50 transition-colors" data-id="element-477" data-mp="1xxf7k7nrw4ku">
                  <GoogleMark className="w-5 h-5" data-id="element-478" data-mp="1e7kqojiimegt" />
                  Continue with Google
                </button>
              </div>

              <div className="flex items-center mb-5" data-id="element-479" data-mp="26yw0ili775cd">
                <div className="flex-1 h-px bg-gray-200" data-id="element-480" data-mp="26xktmctghhh1" />
                <span className="px-3 text-[11px] font-bold uppercase tracking-wider text-gray-400" data-id="element-481" data-mp="1s1v8si00badw">
                  {provider ? 'confirm it’s you' : 'or use name & phone'}
                </span>
                <div className="flex-1 h-px bg-gray-200" data-id="element-482" data-mp="gln8yobymd5x" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-483" data-mp="lkrgzbn5e4uo">
                {!recognized && <div className="grid grid-cols-2 gap-2" data-id="element-484" data-mp="2zs98bc3lr2v8">
                    <Input placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} className="bg-gray-50 border-gray-200 text-gray-900" autoFocus data-id="element-485" data-mp="2zuvep8erbcbv" />
                    <Input placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} className="bg-gray-50 border-gray-200 text-gray-900" data-id="element-486" data-mp="34q126bnt4z22" />
                  </div>}

                <Input placeholder="07X XXX XX XX" type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="bg-gray-50 border-gray-200 text-gray-900" autoFocus={!!recognized} data-id="element-487" data-mp="2l44no2kzoh9t" />

                {recognized && <p className="text-xs text-brand-blue font-medium flex items-center bg-brand-blue/5 rounded-lg px-3 py-2" data-id="element-488" data-mp="3sh6rq6654rxh">
                    <UserRound className="w-3.5 h-3.5 mr-1.5" data-id="element-489" data-mp="61vqpo5jqpcv" />
                    Recognized this number — logging you straight in.
                  </p>}

                {error && <p className="text-red-500 text-xs" data-id="element-490" data-mp="b31injrdu8lc">{error}</p>}

                <Button type="submit" isLoading={isLoading} className="w-full" data-id="element-491" data-mp="39jw27dab9cv8">
                  {provider === 'faceid' ? 'Confirm with Face ID' : recognized ? 'Continue' : 'Log in'}
                  <ArrowRight className="w-5 h-5 ml-2" data-id="element-492" data-mp="3ogefr55vw8xi" />
                </Button>
              </form>

              <p className="text-gray-400 text-[11px] text-center mt-5" data-id="element-493" data-mp="yr2p8n503ey4">
                First time here? Use a staff catch code on the home screen to
                register. We never send SMS codes — your number is only used to
                recognise you.
              </p>
            </div>
          </div>
        </div>}
    </>;
}
function Perk({
  icon: Icon,
  label
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
}) {
  return <div className="flex flex-col items-center gap-1.5 rounded-xl bg-brand-light py-3" data-id="element-494" data-mp="2efitrqpqzg7l">
      <Icon className="w-5 h-5 text-brand-blue" data-id="element-495" data-mp="29go72dont7i7" />
      <span className="text-xs font-bold text-brand-navy" data-id="element-496" data-mp="24kqt2e0vcd8l">{label}</span>
    </div>;
}